import { fetchData, USE_REAL_API, updateLoadingState } from './api';

// API endpoints for news operations
const ENDPOINTS = {
  news: '/news',
  newsById: (id) => `/news/${id}`,
  newsList: (page = 1, limit = 9) => `/news?page=${page}&limit=${limit}`,
  newsByTag: (tag, page = 1, limit = 9) => `/news?tag=${tag}&page=${page}&limit=${limit}`,
  relatedNews: (id) => `/news/${id}/related`,
  newsCategories: () => `/news/categories/`,
};

// Toggle to use mock data or real API
// PRODUCTION: Set this to false when API is ready
// By default use mocks when the global API flag is disabled.
// When `USE_REAL_API` is true we switch off the local news mocks.
export const USE_NEWS_MOCKS = !USE_REAL_API;

// NOTE: If you want to force mocks on/off regardless of the global flag,
// replace the line above with a hard-coded boolean or wire it to an env var.

/**
 * Fetch all news items with pagination
 * 
 * @param {number} page - Page number for pagination
 * @param {number} limit - Number of items per page
 * @returns {Promise<Object>} - News data with pagination info
 * 
 * PRODUCTION: Remove USE_NEWS_MOCKS check when API is fully implemented
 */
export const fetchNewsList = async (page = 1, limit = 9, tag = null) => {
  // PRODUCTION: Remove this check to always use the API
  if (USE_NEWS_MOCKS) {
    console.log('Using mock news data, page:', page, 'limit:', limit, 'tag:', tag);
    
    // Ensure mockNewsItems is properly initialized and has items
    if (!mockNewsItems || mockNewsItems.length === 0) {
      console.warn('Warning: mockNewsItems is empty!');
    } else {
      console.log('Mock items available:', mockNewsItems.length);
    }
    
    // Handle tag filtering
    let filteredNews = tag 
      ? mockNewsItems.filter(item => item.category === tag)
      : mockNewsItems;
    
    // Calculate pagination
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedNews = filteredNews.slice(startIndex, endIndex);
    
    console.log(`Returning ${paginatedNews.length} mock news items`);
    
    // Return paginated data with metadata
    // Map mock items through the same mapper so shapes are consistent with real API
    const mapped = paginatedNews.map(mapNewsBrief);
    return {
      items: mapped,
      pagination: {
        total: filteredNews.length,
        page,
        limit,
        pages: Math.ceil(filteredNews.length / limit)
      }
    };
  }

  updateLoadingState('newsList', true);
  try {
    const endpoint = tag 
      ? ENDPOINTS.newsByTag(tag, page, limit)
      : ENDPOINTS.newsList(page, limit);

    const data = await fetchData(endpoint);

    // DRF-style paginated response: { count, next, previous, results: [...] }
    const results = data?.results || data?.items || [];

    const mapped = results.map(mapNewsBrief);

    const total = data?.count ?? data?.total ?? mapped.length;
    const pages = Math.ceil((total || 0) / limit) || 1;

    return {
      items: mapped,
      pagination: {
        total,
        page,
        limit,
        pages
      }
    };
  } catch (error) {
    console.error('Failed to fetch news list:', error);

    // Fallback to mock data on error (existing behavior)
    let filteredNews = tag 
      ? mockNewsItems.filter(item => item.category === tag)
      : mockNewsItems;

    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedNews = filteredNews.slice(startIndex, endIndex);

    const mappedFallback = paginatedNews.map(mapNewsBrief);
    return {
      items: mappedFallback,
      pagination: {
        total: filteredNews.length,
        page,
        limit,
        pages: Math.ceil(filteredNews.length / limit)
      },
      isErrorFallback: true // Flag to indicate this is fallback data
    };
  } finally {
    updateLoadingState('newsList', false);
  }
};

/**
 * Fetch a single news item by ID
 * 
 * @param {number|string} id - The news item ID
 * @returns {Promise<Object>} - News item data
 * 
 * PRODUCTION: Remove USE_NEWS_MOCKS check when API is fully implemented
 */
export const fetchNewsById = async (id) => {
  // PRODUCTION: Remove this check to always use the API
  if (USE_NEWS_MOCKS) {
    console.log('Using mock data for news ID:', id);
    
    // For the mock data, we'll return the full detailed version of a news item
  const newsItem = mockNewsItems.find(item => item.id === Number(id) || String((item as any).slug) === String(id));
    if (!newsItem) {
      throw new Error(`News with ID ${id} not found`);
    }
    
    // Get the detailed version or generate one
    const detailedNews = mockNewsDetails[id] || generateMockNewsDetail(newsItem);
    return detailedNews;
  }

  updateLoadingState('newsDetail', true);
  try {
    const data = await fetchData(ENDPOINTS.newsById(id));

    // Map API detail to frontend shape
    const mapped = mapNewsDetail(data);
    return mapped;
  } catch (error) {
    console.error(`Failed to fetch news with ID ${id}:`, error);

    // Fallback to mock data on error
    const newsItem = mockNewsItems.find(item => item.id === Number(id));
    if (!newsItem) {
      throw error; // Don't fallback if news not found in mock data
    }

    const detailedNews = mockNewsDetails[id] || generateMockNewsDetail(newsItem);
    return {
      ...detailedNews,
      isErrorFallback: true // Flag to indicate this is fallback data
    };
  } finally {
    updateLoadingState('newsDetail', false);
  }
};

/**
 * Fetch related news for a specific news item
 * 
 * @param {number|string} id - The news item ID to find related news for
 * @param {number} limit - Maximum number of related items to return
 * @returns {Promise<Array>} - List of related news items
 * 
 * PRODUCTION: Remove USE_NEWS_MOCKS check when API is fully implemented
 */
export const fetchRelatedNews = async (id, limit = 2) => {
  // PRODUCTION: Remove this check to always use the API
  if (USE_NEWS_MOCKS) {
    console.log('Using mock data for related news to ID:', id);
    
    // Simple mock implementation: return news from the same category
    const newsItem = mockNewsItems.find(item => item.id === Number(id));
    if (!newsItem) {
      return []; // Return empty array if news not found
    }
    
    // Find news items in the same category, excluding the current one
    const related = mockNewsItems
      .filter(item => item.category === newsItem.category && item.id !== Number(id))
      .slice(0, limit)
      .map(mapNewsBrief);

    return related;
  }

  updateLoadingState('relatedNews', true);
  try {
    const data = await fetchData(ENDPOINTS.relatedNews(id));

    // If the API returns an array, map it; otherwise try to extract results
    const results = Array.isArray(data) ? data : (data?.results || []);
    const mapped = results.map(mapNewsBrief).slice(0, limit);
    return mapped;
  } catch (error) {
    console.error(`Failed to fetch related news for ID ${id}:`, error);

    // Fallback to mock data on error
    const newsItem = mockNewsItems.find(item => item.id === Number(id));
    if (!newsItem) {
      return []; // Return empty array if news not found
    }

    const related = mockNewsItems
      .filter(item => item.category === newsItem.category && item.id !== Number(id))
      .slice(0, limit)
      .map(mapNewsBrief);

    return related;
  } finally {
    updateLoadingState('relatedNews', false);
  }
};

/**
 * Fetch news categories for filters
 * Returns array of categories { id, name, code, description }
 */
export const fetchNewsCategories = async () => {
  updateLoadingState('newsCategories', true);
  try {
    const data = await fetchData(ENDPOINTS.newsCategories());

    // API returns DRF-style paginated response: { count, next, previous, results: [...] }
    const results = data?.results || data || [];

    // Map category objects
    return results.map(cat => ({
      id: cat.id,
      name: cat.name,
      code: cat.code,
      description: cat.description || ''
    }));
  } catch (error) {
    console.error('Failed to fetch news categories:', error);

    // Fallback: infer categories from mock items
    const unique = [];
    mockNewsItems.forEach(item => {
      if (item.category && !unique.find(u => u.name === item.category)) {
        unique.push({ id: null, name: item.category, code: item.category.toLowerCase(), description: '' });
      }
    });
    return unique;
  } finally {
    updateLoadingState('newsCategories', false);
  }
};

/**
 * Helper: pick main image URL from images array
 * Accepts several possible shapes for compatibility with different backends
 */
const pickMainImageUrl = (images) => {
  if (!images || !Array.isArray(images) || images.length === 0) return null;

  // Prefer explicit is_main === true
  const main = images.find(img => img && (img.is_main === true || img.is_main === 'true' || img.is_main === 1));
  if (main && (main.url || main.image || main.path)) return main.url || main.image || main.path;

  // Fallback to first image that has a url-like field
  const first = images.find(img => img && (img.url || img.image || img.path));
  return first ? (first.url || first.image || first.path) : null;
};

/**
 * Форматирование даты публикации в читаемый вид для RU: "25 октября 2025, 17:32"
 * Принимает ISO-строку или другие форматы; если парсинг не удался — возвращает исходное значение.
 */
const formatNewsDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;

  // Дата: '25 октября 2025' (Intl может вернуть '25 октября 2025 г.' — убираем 'г.' )
  const datePart = new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  }).format(d).replace(/\sг\.?$/, '').trim();

  // Время: '17:32' в локальной временной зоне
  const timePart = new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(d);

  return `${datePart}, ${timePart}`;
};

/**
 * Map brief news item from API to frontend shape expected by list page
 */
const mapNewsBrief = (item) => {
  if (!item) return null;

  // Assumptions: API item may contain fields like id, title, excerpt/summary, created_at/published_at, images (array) and category
  const imageUrl = pickMainImageUrl(item.images || item.photos || item.media) || item.image || item.image_url || null;

  return {
    id: item.id,
    slug: item.slug || item.id,
    title: item.title,
    excerpt: item.excerpt || item.summary || item.description || '',
    date: formatNewsDate(item.published_at || item.date || item.created_at || item.updated_at || ''),
    image: imageUrl,
    category: (item.category && (item.category.name || item.category)) || item.tag || '',
    categoryCode: (item.category && (item.category.code || item.category)) || item.tag || ''
  };
};

/**
 * Map detailed news item from API to frontend shape expected by detail page
 */
const mapNewsDetail = (data) => {
  if (!data) return null;

  const imageUrl = pickMainImageUrl(data.images || data.photos || data.media) || data.image || data.image_url || null;

  // Content might be an array of paragraphs or a single HTML/string body
  let content = [];
  if (Array.isArray(data.content)) {
    content = data.content;
  } else if (typeof data.content === 'string' && data.content.trim()) {
    // split by double newlines as paragraph separator
    content = data.content.split(/\n\n+/).map(p => p.trim());
  } else if (typeof data.body === 'string' && data.body.trim()) {
    content = data.body.split(/\n\n+/).map(p => p.trim());
  } else if (data.excerpt) {
    content = [data.excerpt];
  }

  return {
    id: data.id,
    title: data.title,
    content,
    date: formatNewsDate(data.published_at || data.date || data.created_at || ''),
    image: imageUrl,
    category: (data.category && (data.category.name || data.category)) || data.tag || '',
    categoryCode: (data.category && (data.category.code || data.category)) || data.tag || '',
    author: data.author || (data.author_name) || 'Команда AROMA',
    relatedNews: (data.related || data.relatedNews || data.related_news || []).map(mapNewsBrief)
  };
};

/**
 * Helper to generate a detailed news object from a brief one
 * Used only for mock data when a full detailed version isn't available
 * 
 * PRODUCTION: Remove this function when API is fully implemented
 */
const generateMockNewsDetail = (newsItem) => {
  // Generate paragraphs based on excerpt
  const paragraphs = [
    newsItem.excerpt,
    `Продолжение статьи "${newsItem.title}". Этот текст автоматически сгенерирован для демонстрации макета и может быть заменен на реальный контент из API.`,
    `В нашем интернет-магазине вы можете найти различные продукты, связанные с темой этой статьи. Мы предлагаем широкий ассортимент товаров высокого качества.`,
    `Для получения дополнительной информации о наших продуктах и услугах, свяжитесь с нашей службой поддержки или посетите наш офис.`
  ];
  
  // Get 2 random related news items
  const relatedNews = mockNewsItems
    .filter(item => item.id !== newsItem.id)
    .sort(() => 0.5 - Math.random())
    .slice(0, 2);
  
  return {
    ...newsItem,
    content: paragraphs,
    author: "Команда AROMA",
    relatedNews
  };
};

/**
 * Mock news items data
 * Used as fallback and for development without API
 * 
 * PRODUCTION: Consider moving this to a separate file if it gets too large
 */
export const mockNewsItems = [
  {
    id: 1,
    title: "Новая коллекция ароматов «Северное сияние»",
    excerpt: "Представляем новую лимитированную коллекцию, вдохновленную красотой северной природы.",
    date: "15 мая 2023",
    image: "https://img.heroui.chat/image/landscape?w=800&h=500&u=northern1",
    category: "Новинки"
  },
  {
    id: 2,
    title: "Мастер-класс по созданию свечей",
    excerpt: "Приглашаем вас на мастер-класс, где вы сможете создать свою уникальную ароматическую свечу. Мероприятие пройдет в нашем шоу-руме 10 июня. Количество мест ограничено!",
    date: "3 июня 2023",
    image: "https://img.heroui.chat/image/furniture?w=800&h=500&u=workshop1",
    category: "События"
  },
  {
    id: 3,
    title: "Сотрудничество с сетью отелей Premium",
    excerpt: "Мы рады объявить о начале сотрудничества с международной сетью отелей Premium. Теперь гости отелей смогут наслаждаться нашими ароматами в номерах и зонах отдыха.",
    date: "22 июня 2023",
    image: "https://img.heroui.chat/image/furniture?w=800&h=500&u=hotel1",
    category: "Партнерства"
  },
  {
    id: 4,
    title: "Эко-инициатива: переход на биоразлагаемую упаковку",
    excerpt: "В рамках нашей заботы об экологии мы переходим на полностью биоразлагаемую упаковку для всей линейки продукции. Узнайте больше о нашей эко-инициативе.",
    date: "7 июля 2023",
    image: "https://img.heroui.chat/image/landscape?w=800&h=500&u=eco1",
    category: "Экология"
  },
  {
    id: 5,
    title: "Как выбрать аромат для дома в соответствии с сезоном",
    excerpt: "Рассказываем о том, какие ароматы лучше всего подходят для разных времен года. Создайте идеальную атмосферу в вашем доме с помощью сезонных ароматов.",
    date: "18 июля 2023",
    image: "https://img.heroui.chat/image/furniture?w=800&h=500&u=seasons",
    category: "Советы"
  },
  {
    id: 6,
    title: "Открытие нового шоу-рума в Санкт-Петербурге",
    excerpt: "Рады сообщить об открытии нашего нового шоу-рума в историческом центре Санкт-Петербурга. Приглашаем всех на торжественное открытие 15 августа.",
    date: "5 августа 2023",
    image: "https://img.heroui.chat/image/furniture?w=800&h=500&u=showroom",
    category: "События"
  },
  {
    id: 7,
    title: "Интервью с основателем бренда",
    excerpt: "Эксклюзивное интервью с Анной Смирновой, основателем и креативным директором бренда AROMA. О вдохновении, трудностях и планах на будущее.",
    date: "22 августа 2023",
    image: "https://img.heroui.chat/image/fashion?w=800&h=500&u=founder",
    category: "Интервью"
  },
  {
    id: 8,
    title: "Участие в международной выставке Premium Scent",
    excerpt: "Наш бренд представил свою продукцию на международной выставке Premium Scent в Милане. Рассказываем о новых контактах, трендах и впечатлениях.",
    date: "10 сентября 2023",
    image: "https://img.heroui.chat/image/furniture?w=800&h=500&u=exhibit",
    category: "События"
  },
  {
    id: 9,
    title: "Осенняя коллекция ароматов «Золотой лес»",
    excerpt: "Представляем новую осеннюю коллекцию ароматов «Золотой лес». Теплые, уютные композиции с нотами специй, древесины и сухих листьев.",
    date: "25 сентября 2023",
    image: "https://img.heroui.chat/image/landscape?w=800&h=500&u=autumn",
    category: "Новинки"
  },
  {
    id: 10,
    title: "Парфюмерный гардероб: как подбирать ароматы",
    excerpt: "Советы от наших парфюмеров о том, как создать идеальный парфюмерный гардероб для дома на разные случаи и настроения.",
    date: "15 октября 2023",
    image: "https://img.heroui.chat/image/furniture?w=800&h=500&u=perfume1",
    category: "Советы"
  },
  {
    id: 11,
    title: "Зимняя коллекция «Пряный глинтвейн»",
    excerpt: "Встречайте новую зимнюю коллекцию с ароматами корицы, апельсина, гвоздики и красного вина — идеальное сопровождение для уютных зимних вечеров.",
    date: "10 ноября 2023",
    image: "https://img.heroui.chat/image/furniture?w=800&h=500&u=winter1",
    category: "Новинки"
  },
  {
    id: 12,
    title: "Корпоративные подарки к Новому году",
    excerpt: "Представляем каталог корпоративных подарков для ваших клиентов и партнеров. Индивидуальный подход и брендирование по вашему запросу.",
    date: "25 ноября 2023",
    image: "https://img.heroui.chat/image/furniture?w=800&h=500&u=corporate1",
    category: "Партнерства"
  }
];

/**
 * Detailed news content for specific articles
 * Only for the most important or frequently viewed news items
 * 
 * PRODUCTION: Consider moving this to a separate file if it gets too large
 */
export const mockNewsDetails = {
  // Detailed content for news ID 1
  1: {
    id: 1,
    title: "Новая коллекция ароматов «Северное сияние»",
    content: [
      "Мы рады представить нашу новую лимитированную коллекцию «Северное сияние», вдохновленную завораживающей красотой и магией северных пейзажей. Эта уникальная линейка ароматов создана для того, чтобы перенести вас в атмосферу заснеженных лесов, кристально чистого воздуха и мистического сияния северного неба.",
      "Коллекция включает три фирменных аромата, каждый из которых отражает разные аспекты северной природы:",
      "«Морозная хвоя» — свежий, бодрящий аромат с нотами сосны, кедра и можжевельника, дополненный легким акцентом мяты и эвкалипта. Создает ощущение прогулки по заснеженному хвойному лесу ранним зимним утром.",
      "«Ледяные ягоды» — сладко-кислый аромат северных ягод (клюква, брусника, морошка) с нотами ванили и сандала. Теплый, обволакивающий аромат, напоминающий о домашнем уюте среди зимних пейзажей.",
      "«Полярное небо» — воздушный, слегка пряный аромат с нотами мускуса, амбры и легким оттенком специй. Этот аромат передает магию северного сияния — загадочного, мистического и завораживающего.",
      "Вся коллекция представлена в эксклюзивном дизайне — матовое стекло цвета темной ночи с серебристыми элементами, напоминающими переливы северного сияния. Каждый продукт упакован в минималистичную коробку из переработанных материалов с уникальным принтом, изображающим фрагмент северного неба.",
      "Коллекция «Северное сияние» доступна в ограниченном количестве. В линейку входят ароматические свечи, диффузоры и саше, которые можно приобрести как по отдельности, так и в подарочных наборах.",
      "Приглашаем вас открыть для себя магию сера на север с нашей новой коллекцией ароматов «Северное сияние»."
    ],
    date: "15 мая 2023",
    image: "https://img.heroui.chat/image/landscape?w=1200&h=600&u=northern1",
    category: "Новинки",
    author: "Анна Смирнова, основатель AROMA",
    relatedNews: [
      {
        id: 9,
        title: "Осенняя коллекция ароматов «Золотой лес»",
        excerpt: "Представляем новую осеннюю коллекцию ароматов «Золотой лес». Теплые, уютные композиции с нотами специй, древесины и сухих листьев.",
        date: "25 сентября 2023",
        image: "https://img.heroui.chat/image/landscape?w=600&h=400&u=autumn",
        category: "Новинки"
      },
      {
        id: 11,
        title: "Зимняя коллекция «Пряный глинтвейн»",
        excerpt: "Встречайте новую зимнюю коллекцию с ароматами корицы, апельсина, гвоздики и красного вина — идеальное сопровождение для уютных зимних вечеров.",
        date: "10 ноября 2023",
        image: "https://img.heroui.chat/image/furniture?w=600&h=400&u=winter1",
        category: "Новинки"
      }
    ]
  }
};